import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import { EXAMPLES } from "@/data/examples";

const SITE_URL = "https://episodekit.net";

const useJsonLd = (jsonLd) => {
  useEffect(() => {
    if (!jsonLd) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-jsonld", "examples");
    el.text = JSON.stringify(jsonLd);
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [JSON.stringify(jsonLd)]);
};

export default function EpisodeExamplesHub() {
  useSEO({
    title: "Real Episode Output Examples — Inspect Real Deliverables | EpisodeKit",
    description:
      "Browse real podcast and video episodes processed in EpisodeKit and see exactly what the final deliverables look like before you sign up. Full transcripts, show notes, SEO blog posts, social captions, quotes, and YouTube outputs from one upload.",
    path: "/episode-examples",
    image: EXAMPLES[0]?.seo?.ogImage,
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Real Episode Output Examples",
    description:
      "Real deliverables generated by EpisodeKit from uploaded podcast episodes.",
    url: `${SITE_URL}/episode-examples`,
    hasPart: EXAMPLES.map((e) => ({
      "@type": "CreativeWork",
      name: e.title,
      url: `${SITE_URL}/episode-examples/${e.slug}`,
      image: `${SITE_URL}${e.coverImageLarge || e.coverImage}`,
    })),
  });

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="episode-examples-hub"
    >
      <Navbar />

      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-neutral-500 mb-8 flex items-center gap-2"
          >
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-neutral-300">Examples</span>
          </nav>

          {/* Hero */}
          <header className="mb-14 md:mb-16 max-w-3xl">
            <p className="text-purple-400 uppercase text-xs tracking-widest mb-4 font-semibold">
              Real Output Examples
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-unbounded font-bold leading-[1.05] mb-6">
              Real Episode Output Examples
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed">
              Browse real podcast and video episodes processed in EpisodeKit and
              see exactly what the final deliverables look like before you sign
              up. Every output below was generated automatically from one upload
              — no editing, no manual cleanup, no post-production.
            </p>
          </header>

          {/* Examples list */}
          <section
            aria-label="Episode example deliverables"
            className="space-y-6"
          >
            {EXAMPLES.map((ex) => (
              <article
                key={ex.slug}
                data-testid={`hub-example-${ex.slug}`}
                className="group relative rounded-2xl border border-neutral-800 hover:border-purple-500/40 bg-gradient-to-b from-[#15131F] to-[#0E0D17] transition-colors overflow-hidden"
              >
                <Link
                  to={`/episode-examples/${ex.slug}`}
                  className="grid md:grid-cols-[320px_1fr] gap-0"
                >
                  <div className="relative bg-[#0A0913] p-6 flex items-center justify-center">
                    <img
                      src={ex.coverImage}
                      alt={`Cover preview of the EpisodeKit deliverable for ${ex.title}`}
                      loading="lazy"
                      className="w-full max-w-[260px] rounded-lg shadow-xl ring-1 ring-purple-500/10 group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>

                  <div className="p-6 md:p-8 flex flex-col">
                    <p className="text-xs uppercase tracking-widest text-purple-300 font-semibold mb-3">
                      Source · {ex.sourcePodcast}
                    </p>
                    <h2 className="text-2xl md:text-[28px] font-unbounded font-bold mb-3 leading-tight">
                      {ex.title}
                    </h2>
                    <p className="text-neutral-400 leading-relaxed mb-4">
                      Inspect the exact transcript, show notes, SEO blog post,
                      quotes, and 12+ social/YouTube outputs generated from one
                      upload.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-5">
                      {Object.values(ex.meta).map((m) => (
                        <span
                          key={m}
                          className="px-2.5 py-1 rounded-full text-[11px] font-medium text-neutral-300 bg-neutral-800/70 border border-neutral-700/60"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2 mb-6">
                      {ex.landingHighlights.map((o) => (
                        <li
                          key={o}
                          className="flex items-center gap-2 text-sm text-neutral-300"
                        >
                          <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex flex-wrap items-center gap-3">
                      <span className="btn-primary inline-flex items-center gap-2">
                        Preview Example
                        <ArrowRight className="w-4 h-4" />
                      </span>
                      <span className="text-xs text-neutral-500 italic">
                        Generated from a real uploaded episode
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </section>

          {/* CTA */}
          <section className="mt-20 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-unbounded font-semibold mb-4">
              Your episode could be the next one.
            </h2>
            <p className="text-neutral-400 mb-7">
              Upload once. Publish everywhere. One episode becomes your complete
              content distribution system.
            </p>
            <Link to="/register">
              <button
                className="btn-primary inline-flex items-center gap-2 text-base px-8 py-4"
                data-testid="examples-hub-cta"
              >
                Try Your First Episode Free
                <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
